import { useState } from 'react'
import { RefreshCw, X } from 'lucide-react'
import { useVersionSkew } from '../lib/useVersionSkew'

/**
 * Header pill: tells the operator a newer ClickPay build is live and offers
 * a manual refresh. See useVersionSkew.ts for why this exists and how it's
 * detected.
 *
 * Deliberately NEVER auto-reloads — an operator can have an in-progress
 * edit open (a manual_order_qty override, an expanded drawer, an unsaved
 * form) and a surprise reload would lose it. Plain-English copy, no jargon,
 * per the "build ClickPay as a user-facing product" house rule.
 *
 * Dismissal is scoped to the version that triggered it: closing the pill
 * silences THIS deploy's prompt, but if the backend moves again before the
 * operator refreshes, the pill reappears for the new version rather than
 * staying silenced forever.
 */
export default function UpdateAvailablePill() {
  const { stale, remoteVersion } = useVersionSkew()
  const [dismissedVersion, setDismissedVersion] = useState<string | null>(null)

  if (!stale || remoteVersion == null || remoteVersion === dismissedVersion) return null

  return (
    <div
      role="status"
      className="flex items-center gap-2 pl-2.5 pr-1.5 py-1 rounded-full border border-accent-edge bg-accent-soft text-accent-soft-fg text-xs"
    >
      <span>ClickPay was updated — refresh to get the latest fixes</span>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="flex items-center gap-1 font-medium hover:underline shrink-0"
      >
        <RefreshCw size={12} aria-hidden />
        Refresh
      </button>
      <button
        type="button"
        onClick={() => setDismissedVersion(remoteVersion)}
        aria-label="Dismiss update notice"
        className="shrink-0 text-ink-faint hover:text-ink-secondary leading-none px-0.5"
      >
        <X size={13} />
      </button>
    </div>
  )
}
